import TomSelect from 'tom-select';
import { initControls } from './controls/index';

type EnhancedSelect = HTMLSelectElement & { tomselect?: TomSelect };
type Professional = { Id: string; PracticeIds: string[] };

export function initCoverage(root: ParentNode = document) {
  root.querySelectorAll<HTMLElement>('[data-assignment-rule]:not([data-assignment-ready])').forEach(rule => {
    rule.dataset.assignmentReady = 'true';
    const mode = rule.querySelector<HTMLSelectElement>('select[name$=".AssignmentMode"]')!;
    const update = () => {
      rule.querySelectorAll<HTMLElement>('[data-assignment-professional]').forEach(field => {
        field.hidden = field.dataset.assignmentProfessional !== mode.value;
        if (field.hidden) field.querySelector<EnhancedSelect>('select')?.tomselect?.close();
      });
    };
    mode.addEventListener('change', update);
    update();
  });
  root.querySelectorAll<HTMLElement>('[data-coverage-simulation]:not([data-coverage-ready])').forEach(panel => {
    panel.dataset.coverageReady = 'true';
    const scope = panel.querySelector<HTMLFormElement>('[data-simulation-scope]')!;
    scope.addEventListener('change', event => {
      const control = event.target as HTMLSelectElement;
      if (control.name === 'branchId' || control.name === 'circuit') {
        const station = scope.querySelector<HTMLSelectElement>('[name="stationId"]')!;
        station.value = '';
        station.disabled = true;
      }
      panel.querySelector<HTMLElement>('[data-simulation-content]')!.hidden = true;
      panel.querySelector<HTMLElement>('[data-simulation-scope-pending]')!.hidden = false;
    });
    const form = panel.querySelector<HTMLFormElement>('[data-simulation-form]');
    form?.addEventListener('change', () => {
      const result = panel.querySelector<HTMLElement>('[data-simulation-result]');
      if (result) {
        result.hidden = true;
        panel.querySelector<HTMLElement>('[data-simulation-stale]')!.hidden = false;
      }
    });
    form?.addEventListener('submit', () => {
      const submit = form.querySelector<HTMLButtonElement>('[type="submit"]')!;
      submit.disabled = true;
      form.setAttribute('aria-busy', 'true');
    });
  });
  root.querySelectorAll<HTMLElement>('[data-coverage-editor]:not([data-coverage-ready])').forEach(editor => {
    editor.dataset.coverageReady = 'true';
    const functions = editor.querySelector<HTMLElement>('[data-coverage-functions]')!;
    const template = editor.querySelector<HTMLTemplateElement>('[data-coverage-function-template]')!;
    const professionals: Professional[] = JSON.parse(editor.querySelector('[data-coverage-professionals]')?.textContent ?? '[]');
    const professional = editor.querySelector<HTMLSelectElement>('[data-coverage-professional]')!;
    const filterPractices = () => {
      const allowed = new Set(professionals.find(p => p.Id === professional.value)?.PracticeIds ?? []);
      functions.querySelectorAll<EnhancedSelect>('[data-coverage-practices]').forEach(select => {
        for (const option of Array.from(select.options)) {
          option.disabled = !allowed.has(option.value);
        }
        if (select.tomselect) for (const [value, option] of Object.entries(select.tomselect.options)) {
          select.tomselect.updateOption(value, { ...option, disabled: !allowed.has(value) });
        }
        select.tomselect?.refreshOptions(false);
      });
    };
    let next = functions.children.length;
    editor.querySelector('[data-add-coverage-function]')?.addEventListener('click', () => {
      if (functions.children.length >= 32) return;
      const fragment = template.content.cloneNode(true) as DocumentFragment;
      const key = `new${next++}`;
      fragment.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[name]').forEach(input => {
        input.name = input.name.replaceAll('__row__', key);
        if (input.name === 'Input.Functions.Index') input.value = key;
      });
      const row = fragment.firstElementChild as HTMLElement;
      functions.append(fragment);
      initControls(row);
      filterPractices();
      row.querySelector<HTMLSelectElement>('select')?.focus();
    });
    functions.addEventListener('click', event => {
      const button = (event.target as Element).closest('[data-remove-coverage-function]');
      const row = button?.closest('[data-coverage-function]');
      if (!row || functions.children.length <= 1) return;
      row.querySelectorAll<EnhancedSelect>('select').forEach(select => select.tomselect?.destroy());
      row.remove();
    });
    professional.addEventListener('change', filterPractices);
    initControls(functions);
    filterPractices();
  });
  root.querySelectorAll<HTMLFormElement>('[data-coverage-exception]:not([data-coverage-ready])').forEach(form => {
    form.dataset.coverageReady = 'true';
    const all = form.querySelector<HTMLInputElement>('[data-all-coverage-stations]')!;
    const stations = Array.from(form.querySelectorAll<HTMLInputElement>('[data-coverage-station]'));
    const updateStations = () => stations.forEach(input => { input.disabled = all.checked; if (all.checked) input.checked = false; });
    all.addEventListener('change', updateStations);
    const replacement = form.querySelector<HTMLSelectElement>('[data-coverage-replacement]')!;
    const room = form.querySelector<HTMLSelectElement>('[data-coverage-replacement-room]')!;
    const updateReplacement = () => { room.disabled = !replacement.value; if (!replacement.value) room.value = ''; };
    replacement.addEventListener('change', updateReplacement);
    form.addEventListener('submit', event => {
      if (!all.checked && !stations.some(input => input.checked)) {
        event.preventDefault();
        all.setCustomValidity('Selecciona al menos una estacion o marca todas.');
        all.reportValidity();
      }
    });
    form.addEventListener('change', () => all.setCustomValidity(''));
    updateStations();
    updateReplacement();
  });
}
document.addEventListener('DOMContentLoaded', () => initCoverage());
window.addEventListener('pageshow', () => {
  document.querySelectorAll<HTMLFormElement>('[data-simulation-form][aria-busy]').forEach(form => {
    form.removeAttribute('aria-busy');
    form.querySelector<HTMLButtonElement>('[type="submit"]')!.disabled = false;
  });
});
document.body.addEventListener('htmx:afterSwap', (event: Event) => initCoverage(event.target as Element));
